import { Trans } from '@lingui/react/macro';
import type { CustomerReferredFriendRecord } from '@navio/nsdk';
import { cn, cva } from 'noddi-ui-common';
import { toReferralStaffStatus, type ReferralStaffStatus } from 'noddi-web';
import type { ReactNode } from 'react';

type Props = {
  friend: CustomerReferredFriendRecord;
  showHeading?: boolean;
};

type TimelineDotState = 'done' | 'pending' | 'expired' | 'muted';

type TimelineStep = {
  title: ReactNode;
  state: TimelineDotState;
};

const timelineDotVariants = cva('mt-1 size-2.5 shrink-0 rounded-full', {
  variants: {
    state: {
      done: 'bg-primary',
      pending: 'border-2 border-primary bg-background',
      expired: 'bg-danger',
      muted: 'bg-muted-foreground/30'
    }
  }
});

export const CustomerReferralJourneyTimeline = ({ friend, showHeading = true }: Props) => {
  const staffStatus: ReferralStaffStatus = toReferralStaffStatus(friend.status);
  const steps: TimelineStep[] = [
    {
      title: <Trans>Your friend activated the invitation</Trans>,
      state: 'done'
    }
  ];

  if (staffStatus === 'expired') {
    steps.push(
      {
        title: <Trans>Invitation expired before order</Trans>,
        state: 'expired'
      },
      {
        title: <Trans>Reward</Trans>,
        state: 'muted'
      }
    );
  } else if (staffStatus === 'waiting') {
    steps.push(
      {
        title: <Trans>Waiting for completed order</Trans>,
        state: 'pending'
      },
      {
        title: <Trans>Reward</Trans>,
        state: 'muted'
      }
    );
  } else {
    steps.push(
      {
        title: <Trans>Completed order and paid</Trans>,
        state: 'done'
      },
      {
        title: <Trans>Your reward was given</Trans>,
        state: friend.rewardIssuedAt != null ? 'done' : 'pending'
      }
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      {showHeading && (
        <span className='text-xs font-semibold tracking-wider text-muted-foreground uppercase'>
          <Trans>Status</Trans>
        </span>
      )}
      <ol className='flex flex-col'>
        {steps.map((step, index) => (
          <li key={index} className='flex gap-3'>
            <div className='flex flex-col items-center'>
              <span className={timelineDotVariants({ state: step.state })} />
              {index < steps.length - 1 && <span className='w-px flex-1 bg-border' />}
            </div>
            <div className={cn('flex min-w-0 flex-col gap-1', index < steps.length - 1 && 'pb-4')}>
              <span
                className={cn('text-sm leading-tight font-medium', step.state === 'pending' && 'text-muted-foreground')}
              >
                {step.title}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};
